/**
 * Type definitions for Zielgruppe (Target Group) data
 */

/**
 * Persona data
 */
export interface PersonaData {
  image: string;
  name: string;
  prozent: number;
  beschreibung: string;
}

/**
 * Altersspektrum (Age spectrum) item
 */
export interface AltersspektrumItem {
  Zielgruppe_Altersspektrum_item_name: string;
  Zielgruppe_Altersspektrum_item_prozent: number;
}

/**
 * Bildungscluster (Education cluster) item
 */
export interface BildungsclusterItem {
  Zielgruppe_Bildungscluster_item_name: string;
  Zielgruppe_Bildungscluster_item_prozent: number;
}

/**
 * Bildungsniveau (Education level) item
 */
export interface BildungsniveauItem {
  Zielgruppe_Bildungsniveau_item_name: string;
  Zielgruppe_Bildungsniveau_item_prozent: number;
}

/**
 * Berufliche Abschlüsse (Professional degrees) item
 */
export interface BeruflicheAbschluesseItem {
  Zielgruppe_Berufliche_Abschluesse_item_name: string;
  Zielgruppe_Berufliche_Abschluesse_item_prozent: number;
}

/**
 * Akademische Abschlüsse (Academic degrees) item
 */
export interface AkademischeAbschluesseItem {
  Zielgruppe_Akademische_Abschluesse_item_name: string;
  Zielgruppe_Akademische_Abschluesse_item_prozent: number;
}

/**
 * Touchpoints item
 */
export interface TouchpointsItem {
  Zielgruppe_Touchpoints_item_trigger: string;
  Zielgruppe_Touchpoints_item_RuV: string;
  Zielgruppe_Touchpoints_item_EuA: string;
  Zielgruppe_Touchpoints_item_After_Sales: string;
  Zielgruppe_Touchpoints_item_Wiederkauf: string;
}

/**
 * Persona details
 */
export interface PersonaDetails {
  Name: string;
  Alter: number;
  Geschlecht: string;
  Bildungsniveau: string;
  Berufsstatus: string;
  Einkommen: number;
  Haushaltsform: string;
  Relevanz: string;
  Lebensphase: string;
  Kaufmotive: string;
  Painpoints: string;
}

/**
 * Main Zielgruppe data structure from API
 */
export interface ZielgruppeData {
  // Personas
  Zielgruppe_Persona_1_image: string;
  Zielgruppe_Persona_1_name: string;
  Zielgruppe_Persona_1_prozent: number;
  Zielgruppe_Persona_1_beschreibung: string;
  Zielgruppe_Persona_2_image: string;
  Zielgruppe_Persona_2_name: string;
  Zielgruppe_Persona_2_prozent: number;
  Zielgruppe_Persona_2_beschreibung: string;
  Zielgruppe_Persona_3_image: string;
  Zielgruppe_Persona_3_name: string;
  Zielgruppe_Persona_3_prozent: number;
  Zielgruppe_Persona_3_beschreibung: string;

  // Altersspektrum
  Zielgruppe_Altersspektrum_array: AltersspektrumItem[];
  Zielgruppe_Altersspektrum_median: number;

  // Bildungscluster
  Zielgruppe_Bildungscluster_array: BildungsclusterItem[];

  // Persona details
  Zielgruppe_Persona_details: PersonaDetails;

  // Bildungsniveau
  Zielgruppe_Bildungsniveau_array: BildungsniveauItem[];

  // Berufliche Abschlüsse
  Zielgruppe_Berufliche_Abschluesse_array: BeruflicheAbschluesseItem[];

  // Akademische Abschlüsse
  Zielgruppe_Akademische_Abschluesse_array: AkademischeAbschluesseItem[];

  // Touchpoints
  Zielgruppe_Touchpoints_array: TouchpointsItem[];

  // Zahlungsbereitschaft
  Zielgruppe_Zahlungsbereitschaft_Preisspanne_von: number;
  Zielgruppe_Zahlungsbereitschaft_Preisspanne_bis: number | string;
  Zielgruppe_Zahlungsbereitschaft_Zahlungsmodell: string;
  Zielgruppe_Zahlungsbereitschaft_Rabatte: string;
  Zielgruppe_Zahlungsbereitschaft_Zahlungszeitpunkte: string;

  // Summary
  Zielgruppe_Zusammenfassung: string;
}

/**
 * Combined data structure for card and overlay
 */
export interface ZielgruppeFullData {
  main: ZielgruppeData;
  personas: PersonaData[];
}

/**
 * Parse raw JSON string from API into structured Zielgruppe data
 */
export function parseZielgruppeData(jsonString: string): ZielgruppeFullData | null {
  try {
    let data = jsonString;

    // If the string is wrapped in quotes, try to parse it first
    if (typeof data === 'string' && (data.startsWith('"') || data.startsWith("'"))) {
      try {
        data = JSON.parse(data);
      } catch {
        // If it fails, continue with original string
      }
    }

    const parsed = typeof data === 'string' ? JSON.parse(data) : data;

    // Handle case where it's an array
    const mainData = Array.isArray(parsed) ? parsed[0] : parsed;

    if (!mainData) {
      return null;
    }

    // Extract personas from main data
    const personas: PersonaData[] = [
      {
        image: mainData.Zielgruppe_Persona_1_image || '',
        name: mainData.Zielgruppe_Persona_1_name || '',
        prozent: mainData.Zielgruppe_Persona_1_prozent || 0,
        beschreibung: mainData.Zielgruppe_Persona_1_beschreibung || '',
      },
      {
        image: mainData.Zielgruppe_Persona_2_image || '',
        name: mainData.Zielgruppe_Persona_2_name || '',
        prozent: mainData.Zielgruppe_Persona_2_prozent || 0,
        beschreibung: mainData.Zielgruppe_Persona_2_beschreibung || '',
      },
      {
        image: mainData.Zielgruppe_Persona_3_image || '',
        name: mainData.Zielgruppe_Persona_3_name || '',
        prozent: mainData.Zielgruppe_Persona_3_prozent || 0,
        beschreibung: mainData.Zielgruppe_Persona_3_beschreibung || '',
      },
    ].filter(p => p.name && p.prozent > 0);

    return {
      main: mainData as ZielgruppeData,
      personas,
    };
  } catch {
    return null;
  }
}

/**
 * Generate mock data for development
 */
export function getMockZielgruppeData(): ZielgruppeFullData {
  const mockData: ZielgruppeData = {
    Zielgruppe_Persona_1_image: '/persona1.png',
    Zielgruppe_Persona_1_name: 'Familien',
    Zielgruppe_Persona_1_prozent: 35,
    Zielgruppe_Persona_1_beschreibung: 'Familien mit Kindern, die Wert auf Qualität, Sicherheit und ein gutes Preis-Leistungs-Verhältnis legen.',
    Zielgruppe_Persona_2_image: '/persona2.png',
    Zielgruppe_Persona_2_name: 'Eco-Performer',
    Zielgruppe_Persona_2_prozent: 28,
    Zielgruppe_Persona_2_beschreibung: 'Umweltbewusste Leistungsträger, die nachhaltige Produkte bevorzugen und bereit sind, dafür mehr zu investieren.',
    Zielgruppe_Persona_3_image: '/persona3.png',
    Zielgruppe_Persona_3_name: 'Best-Ager',
    Zielgruppe_Persona_3_prozent: 22,
    Zielgruppe_Persona_3_beschreibung: 'Erfahrene Konsumenten 55+, die Komfort und Zuverlässigkeit schätzen und markentreu einkaufen.',
    Zielgruppe_Altersspektrum_array: [
      { Zielgruppe_Altersspektrum_item_name: '18-24', Zielgruppe_Altersspektrum_item_prozent: 8 },
      { Zielgruppe_Altersspektrum_item_name: '25-34', Zielgruppe_Altersspektrum_item_prozent: 22 },
      { Zielgruppe_Altersspektrum_item_name: '35-44', Zielgruppe_Altersspektrum_item_prozent: 28 },
      { Zielgruppe_Altersspektrum_item_name: '45-54', Zielgruppe_Altersspektrum_item_prozent: 24 },
      { Zielgruppe_Altersspektrum_item_name: '55-64', Zielgruppe_Altersspektrum_item_prozent: 12 },
      { Zielgruppe_Altersspektrum_item_name: '65+', Zielgruppe_Altersspektrum_item_prozent: 6 },
    ],
    Zielgruppe_Altersspektrum_median: 28.5,
    Zielgruppe_Bildungscluster_array: [
      { Zielgruppe_Bildungscluster_item_name: 'Akademiker', Zielgruppe_Bildungscluster_item_prozent: 42 },
      { Zielgruppe_Bildungscluster_item_name: 'Facharbeiter', Zielgruppe_Bildungscluster_item_prozent: 35 },
      { Zielgruppe_Bildungscluster_item_name: 'Angestellte', Zielgruppe_Bildungscluster_item_prozent: 23 },
    ],
    Zielgruppe_Persona_details: {
      Name: 'Familie Müller',
      Alter: 38,
      Geschlecht: 'weiblich',
      Bildungsniveau: 'Hochschulabschluss',
      Berufsstatus: 'Angestellt',
      Einkommen: 4250,
      Haushaltsform: '4-Personen-Haushalt',
      Relevanz: 'Sehr hoch',
      Lebensphase: 'Familiengründung',
      Kaufmotive: 'Qualität, Nachhaltigkeit, Familienfreundlichkeit',
      Painpoints: 'Zeitmangel, Preissensibilität bei Großeinkäufen',
    },
    Zielgruppe_Bildungsniveau_array: [
      { Zielgruppe_Bildungsniveau_item_name: 'Hochschulabschluss', Zielgruppe_Bildungsniveau_item_prozent: 45 },
      { Zielgruppe_Bildungsniveau_item_name: 'Abitur', Zielgruppe_Bildungsniveau_item_prozent: 30 },
      { Zielgruppe_Bildungsniveau_item_name: 'Mittlere Reife', Zielgruppe_Bildungsniveau_item_prozent: 25 },
    ],
    Zielgruppe_Berufliche_Abschluesse_array: [
      { Zielgruppe_Berufliche_Abschluesse_item_name: 'Kaufmännisch', Zielgruppe_Berufliche_Abschluesse_item_prozent: 38 },
      { Zielgruppe_Berufliche_Abschluesse_item_name: 'Technisch', Zielgruppe_Berufliche_Abschluesse_item_prozent: 32 },
      { Zielgruppe_Berufliche_Abschluesse_item_name: 'Handwerklich', Zielgruppe_Berufliche_Abschluesse_item_prozent: 30 },
    ],
    Zielgruppe_Akademische_Abschluesse_array: [
      { Zielgruppe_Akademische_Abschluesse_item_name: 'Bachelor', Zielgruppe_Akademische_Abschluesse_item_prozent: 45 },
      { Zielgruppe_Akademische_Abschluesse_item_name: 'Master', Zielgruppe_Akademische_Abschluesse_item_prozent: 35 },
      { Zielgruppe_Akademische_Abschluesse_item_name: 'Promotion', Zielgruppe_Akademische_Abschluesse_item_prozent: 20 },
    ],
    Zielgruppe_Touchpoints_array: [
      {
        Zielgruppe_Touchpoints_item_trigger: 'Online-Recherche',
        Zielgruppe_Touchpoints_item_RuV: 'Website, Social Media',
        Zielgruppe_Touchpoints_item_EuA: 'Produktvergleiche, Bewertungen',
        Zielgruppe_Touchpoints_item_After_Sales: 'E-Mail Newsletter, Support',
        Zielgruppe_Touchpoints_item_Wiederkauf: 'Treueprogramm, Rabatte',
      },
    ],
    Zielgruppe_Zahlungsbereitschaft_Preisspanne_von: 50,
    Zielgruppe_Zahlungsbereitschaft_Preisspanne_bis: 200,
    Zielgruppe_Zahlungsbereitschaft_Zahlungsmodell: 'Einmalzahlung, Ratenzahlung',
    Zielgruppe_Zahlungsbereitschaft_Rabatte: 'Mengenrabatte, Treuerabatte',
    Zielgruppe_Zahlungsbereitschaft_Zahlungszeitpunkte: 'Sofort, 30 Tage',
    Zielgruppe_Zusammenfassung: 'Die Zielgruppe besteht hauptsächlich aus Familien mit mittlerem bis hohem Einkommen, die Wert auf Qualität und Nachhaltigkeit legen. Der typische Kunde ist zwischen 35 und 44 Jahre alt, gut ausgebildet und in einer stabilen Lebenssituation.',
  };

  return {
    main: mockData,
    personas: [
      { image: mockData.Zielgruppe_Persona_1_image, name: mockData.Zielgruppe_Persona_1_name, prozent: mockData.Zielgruppe_Persona_1_prozent, beschreibung: mockData.Zielgruppe_Persona_1_beschreibung },
      { image: mockData.Zielgruppe_Persona_2_image, name: mockData.Zielgruppe_Persona_2_name, prozent: mockData.Zielgruppe_Persona_2_prozent, beschreibung: mockData.Zielgruppe_Persona_2_beschreibung },
      { image: mockData.Zielgruppe_Persona_3_image, name: mockData.Zielgruppe_Persona_3_name, prozent: mockData.Zielgruppe_Persona_3_prozent, beschreibung: mockData.Zielgruppe_Persona_3_beschreibung },
    ],
  };
}
